/**
 * API contracts — the wire shapes shared between the dashboard server and
 * its web client. Keep these flat and serializable. No date objects, only
 * ISO-8601 strings.
 */
import type { Permission, Role, Section } from "./permissions.js";

/** Augmented JWT claim set the dashboard issues. Strict superset of the
 * upstream support-center claim shape so the same token still authenticates
 * directly against the support API. */
export interface DashboardJwtClaims {
  /** Subject = support-center user id (cross-app stable identifier). */
  sub: string;
  user_id: number;
  email: string;
  name: string;
  /** Upstream support-center role. */
  upstream_role: "user" | "support" | "admin" | "super_admin";
  /** Dashboard role — orthogonal to the upstream role. */
  dashboard_role: Role;
  /** Resolved permission set (what every check runs against). */
  permissions: Permission[];
  /** Org membership, copied from upstream so the embedded portals
   * receive a token they recognize. */
  org_ids: number[];
  /** Token version for revocation — matches upstream. */
  v: number;
  /** Set when the upstream account has no second factor enrolled. While
   * true the dashboard server refuses every route except the auth +
   * self-service enrollment surface (the staff-side mirror of portal-api
   * `require_two_factor_enrolled!`). Optional for rollout safety: tokens
   * minted before this shipped lack the claim and are treated as
   * enrolled (they expire within the 30-minute access TTL). */
  must_enroll?: boolean;
  /** Set when the account signed in with a one-time invite password and
   * must set its own before doing anything else. Gated like `must_enroll`,
   * and checked FIRST (set password → enroll 2FA → dashboard). Optional for
   * rollout safety (pre-claim tokens are treated as not-required). */
  must_change?: boolean;
  /** Token type / lifetimes — same as upstream so refresh logic is one
   * implementation. */
  type: "access" | "refresh";
  iat: number;
  exp: number;
  jti: string;
}

export interface SessionUser {
  id: number;
  email: string;
  name: string;
  initials: string;
  upstream_role: DashboardJwtClaims["upstream_role"];
  dashboard_role: Role;
  permissions: Permission[];
  org_ids: number[];
  avatar_url: string | null;
  email_verified: boolean;
  /** True when the account has no second factor enrolled. The web client
   * routes these users into the 2FA setup flow; the server independently
   * enforces the same via the `must_enroll` token claim. */
  must_enroll_two_factor: boolean;
  /** True when the account must set its own password (signed in with a
   * one-time invite password). The client routes these users into the
   * set-password flow FIRST (before 2FA); the server enforces via the
   * `must_change` token claim. Optional for rollout safety — a session
   * minted before this shipped lacks it and is treated as not-required. */
  must_change_password?: boolean;
}

export interface LoginRequest {
  email: string;
  password: string;
  /** TOTP — required if 2FA is enrolled on the upstream account. */
  otp?: string;
}

export interface LoginResponse {
  user: SessionUser;
  access_token: string;
  refresh_token: string;
  expires_in: number;
}

export interface TwoFactorRequiredResponse {
  two_factor_required: true;
  user_id: number;
  /** Which second factor the account uses — drives the sign-in challenge
   * copy ("we emailed a code" vs "open your authenticator"). portal-api
   * sends it on the challenge envelope; absent ⇒ treat as `"totp"`. */
  two_factor_method?: "totp" | "email";
  /** For the email method, the address portal-api just sent the code to,
   * so the challenge step can tell the user where to look. Absent / null
   * for TOTP (nothing was emailed). */
  email_hint?: string | null;
}

export interface RefreshRequest {
  refresh_token: string;
}

export interface ApiErrorBody {
  error: string;
  code?: string;
  details?: unknown;
}

/* ---------- dashboard aggregation ----------------------------------- */

export interface DashboardOverview {
  generated_at: string;
  /** Per-section health + headline metrics. Order is stable so the web
   * grid doesn't reflow between polls. */
  cards: DashboardCard[];
}

/* ---------- dashboard pulse (live company-wide page) ---------------- */

/**
 * The payload behind `/api/dashboard/pulse` — composed once per refresh
 * tick from the cross-system aggregator, the local server-events
 * history, and direct fetches into the support / sales / docs
 * upstreams. Designed so the web page can render in one round-trip.
 */
export interface DashboardPulse {
  generated_at: string;
  headline: PulseHeadline;
  systems: PulseSystem[];
  /** Summary of the user-curated server list shown on `/server`.
   * Distinct from `systems[]`, which is the small fixed set of
   * application servers (Support API, CRM, AI Gateway, …). This is
   * the wide list (any number of monitored hosts). Null when the user
   * doesn't have `server:view`. */
  monitored_servers: MonitoredServersSummary | null;
  queues: PulseQueues;
  /** Soft channel — non-fatal upstream errors collected this tick. The
   * page still renders; this lets us surface which feeds were empty
   * "for a reason" instead of looking like a quiet day. */
  upstream_errors: Array<{ source: string; message: string }>;
}

export interface MonitoredServersSummary {
  healthy: number;
  total: number;
  /** When at least one server has been probed at least once; null on
   * first boot before any sweep has finished. */
  last_sweep_at: string | null;
}

export interface PulseHeadline {
  /** Human-readable one-liner: "All systems healthy. 12 open tickets,
   * 3 breaching SLA, 7 new leads in 24h." */
  sentence: string;
  /** Drives the banner tint on the web side. */
  mood: "calm" | "watch" | "alert";
}

export interface PulseSystem {
  /** Stable id — also doubles as the in-app drill-down target where
   * applicable. */
  id: string;
  label: string;
  status: HealthStatus;
  latency_ms: number | null;
}

export interface PulseQueues {
  sla_breaches: QueueRow[];
  new_tickets: QueueRow[];
  leads_pending: QueueRow[];
  /** Marketing drafts awaiting human review (status=in_review on the
   * marketing app). Empty without `marketing:view` or when
   * MARKETING_APP_URL is unset. No longer a pulse CARD (replaced by
   * "Overdue quotes", 2026-09-15) — still computed because it drives
   * the `marketing_review` notification. */
  marketing_review: QueueRow[];
  /** Customer invoices that are past due — the most operationally
   * urgent CRM signal. Sorted oldest-due first server-side. (The
   * former "Recent tasks" `crm_activity` queue was removed from the
   * dashboard pulse — that surface lives only at `/crm/tasks`.) */
  invoices_due: QueueRow[];
  /** Quotes whose validity date has passed with no decision (still
   * pending / expired — never accepted, converted or rejected), most
   * recently lapsed first: the ones still worth a follow-up. Rows carry
   * the quote total, so this is behind the financial wall — empty
   * without `crm:view` + `crm:financials`. */
  quotes_overdue: QueueRow[];
  /** Last 5 customer↔Otto conversations by activity (any status).
   * Pure usage visibility — empty without `support:view`. */
  recent_conversations: QueueRow[];
  /** Sales leads whose dated buying window is closing soon — a good-fit,
   * un-contacted lead whose deadline is within the "super hot" window
   * (≤14 days, the ≤3-day "last chance" leads first). The "super hot mode"
   * signal. Each row's `id` is `${leadId}:${tier}` so the notification
   * dispatcher fires once per tier transition. Empty without `sales:view` or
   * when SALES_AGENT_URL is unset. */
  leads_closing_soon: QueueRow[];
}

/**
 * Canonical map from each {@link PulseQueues} field to the `source`
 * string stamped on its {@link DashboardPulse.upstream_errors} entry
 * when that queue's upstream fetch fails (`safeSource(source, …)` in
 * the server pulse composer).
 *
 * Single source of truth shared by the server (`services/pulse.ts`,
 * which passes these as the `safeSource` source) and the web
 * `NeedsAttention` grid, which uses it to tell a *genuinely* empty
 * queue ("all clear", green) apart from one that's empty only because
 * its upstream errored this tick ("couldn't reach", amber). Without
 * this an unreachable system (e.g. crm-api down) renders a
 * falsely-reassuring "No overdue invoices." while invoices are in fact
 * overdue. The `satisfies` clause forces this map to stay in lockstep
 * with `PulseQueues` — add a queue and TS makes you add its source.
 *
 * (The headline-only `sales_new_24h` count is NOT a queue, so it is
 * deliberately absent here.)
 */
export const PULSE_SOURCES = {
  sla_breaches: "support_sla_breaches",
  new_tickets: "support_new_tickets",
  leads_pending: "sales_pending",
  marketing_review: "marketing_review",
  invoices_due: "crm_invoices_due",
  quotes_overdue: "crm_quotes_overdue",
  recent_conversations: "support_recent_conversations",
  leads_closing_soon: "sales_closing_soon",
} as const satisfies Record<keyof PulseQueues, string>;

/** A `PulseQueues` field name (and key into {@link PULSE_SOURCES}). */
export type PulseQueueKey = keyof typeof PULSE_SOURCES;

export interface QueueRow {
  id: string;
  label: string;
  meta?: string | undefined;
  link?: string | undefined;
  tone?: "good" | "bad" | "neutral" | undefined;
}

export interface DashboardCard {
  section: Section;
  label: string;
  /** Reachable + reporting fresh data. */
  status: HealthStatus;
  primary: DashboardMetric;
  secondary: DashboardMetric[];
  /** When the card was sourced — useful when an upstream is degraded
   * and we're showing cached numbers. */
  as_of: string;
  /** Optional — populated for cards whose upstream errored. */
  error?: string | undefined;
}

export interface DashboardMetric {
  key: string;
  label: string;
  value: number | string;
  /** Optional comparison vs. the previous period. Negative is a drop. */
  delta?: number | undefined;
  delta_label?: string | undefined;
  /** UI hint — "good", "bad", "neutral" — server decides so the web
   * doesn't bake business rules. */
  tone?: "good" | "bad" | "neutral" | undefined;
}

/* ---------- system health ------------------------------------------- */

export type HealthStatus = "healthy" | "degraded" | "down" | "unknown";

export interface SystemStatus {
  generated_at: string;
  systems: SystemHealth[];
}

export interface SystemHealth {
  /** Stable id — also used as the section / nav target where applicable. */
  id: string;
  label: string;
  status: HealthStatus;
  /** ms — last successful round-trip. null while the first probe is in
   * flight or after a hard failure. */
  latency_ms: number | null;
  url: string;
  checked_at: string;
  /** Free-form upstream payload — e.g. Postgres + pgvector + Redis report
   * from `/health` on the AI Gateway. Keep it unstructured so we don't have
   * to update the contract every time a system adds a sub-check. */
  details?: Record<string, unknown> | undefined;
  error?: string | undefined;
}

/* ---------- public user profile (peer lookup) ----------------------- */

/**
 * The small, non-sensitive projection of a staff user returned by
 * `GET /api/users/:id` and rendered in the click-to-view profile card. Any
 * authenticated staff user may read any other user's profile — so it carries
 * ONLY identity + role, never permissions, 2FA, org ids, or any CRM/monetary
 * data. Sourced from the dashboard's own user store (no upstream round-trip);
 * `name` falls back to the email when the JWT-captured name is still null.
 */
export interface PublicUserProfile {
  id: number;
  name: string;
  email: string;
  dashboard_role: Role;
}

/* ---------- admin: users + permissions ------------------------------ */

export interface DashboardUser {
  id: number;
  email: string;
  name: string;
  upstream_role: DashboardJwtClaims["upstream_role"];
  dashboard_role: Role;
  permissions: Permission[];
  active: boolean;
  last_seen_at: string | null;
  /** Which client they last used: a desktop app version (e.g. "0.1.23"),
   * "web" for the browser, or null until seen since this shipped. Captured
   * from the x-client-version request header. */
  client_version: string | null;
}

/**
 * Pre-provisioned dashboard account — recorded by an admin BEFORE the
 * user has signed in. Consumed atomically on first sign-in: the new
 * `dashboard_users` row inherits this invite's role + overrides instead
 * of the default `viewer`. Until consumed, an invite has no effect on
 * any permission check — it's just an intent the dashboard remembers.
 */
export interface DashboardUserInvite {
  id: number;
  email: string;
  /** Display name captured at invite time, so the pending row shows the
   * person we invited (e.g. "New Hire") rather than just their email.
   * Null when the admin invited by email only. */
  name: string | null;
  dashboard_role: Role;
  /** Resolved permission set (role bundle + overrides) — same shape as
   * `DashboardUser.permissions` so the admin UI can render both in one
   * table without a discriminated-union dance. */
  permissions: Permission[];
  invited_by_user_id: number | null;
  invited_by_email: string | null;
  created_at: string;
}

export interface ListDashboardUsersResponse {
  users: DashboardUser[];
  invites: DashboardUserInvite[];
}

export interface CreateDashboardUserInviteRequest {
  email: string;
  /** Optional display name for the invitee. Split into first/last for
   * the portal-api identity so the invitation email greets them by name
   * (fixes the "Hi <inviter>" bug) and the pending row shows who it's for. */
  name?: string;
  dashboard_role: Role;
  /** Optional per-permission overrides on top of the role bundle. */
  permission_overrides?: Permission[];
}

/**
 * One row in the /admin/roles matrix — for each built-in role, the
 * baseline permission set (from the shared matrix in
 * `packages/shared/src/permissions.ts`), the *effective* permission
 * set (baseline + any admin customisation), whether the role has
 * been customised, and whether it's editable from the UI at all.
 * `super_admin` is `protected: true` — its bundle is always every
 * permission, by definition, and the API refuses to mutate it.
 */
export interface RoleDefinition {
  role: Role;
  label: string;
  baseline_permissions: Permission[];
  effective_permissions: Permission[];
  customized: boolean;
  protected: boolean;
  updated_at: string | null;
  updated_by_email: string | null;
}

export interface ListRolesResponse {
  roles: RoleDefinition[];
}

export interface UpdateRolePermissionsRequest {
  /** Replaces the entire effective bundle for this role. Pass an empty
   * array to make the role effectively powerless. To restore the
   * shipped baseline, hit `DELETE /api/admin/roles/:role/permissions`
   * instead. */
  permissions: Permission[];
}

export interface UpdateUserRoleRequest {
  dashboard_role: Role;
}

export interface UpdateUserPermissionsRequest {
  /** Explicit permission overrides for this user. Replaces the per-user
   * grant list. To go back to "role-only", pass an empty array. */
  permissions: Permission[];
}

/* ---------- embedded-portal SSO bridge ------------------------------ */

/** When the web app needs to embed the Support Portal or CRM in an iframe,
 * it requests a one-time bridge ticket from the dashboard server. The
 * server signs `{ user_id, target, exp }` so the embedded app can verify
 * the ticket against the shared `JWT_SECRET` and start a session without
 * re-prompting the user. */
export interface EmbedTicketRequest {
  /** Documentation used to be embed-only here; the dashboard's
   * Documentation tab is now native and talks to the support-center API
   * directly via the `/api/documentation/*` proxy. */
  target: "support" | "crm";
}

export interface EmbedTicketResponse {
  /** Short-lived (≤ 60s) JWT, type=embed_ticket. */
  ticket: string;
  /** Where to load the iframe. The query string already contains the
   * ticket so the web side just sets `iframe.src = redirect_url`. */
  redirect_url: string;
  expires_in: number;
}

/* ---------- monitored servers (Server Status: left side) ----------- */

/**
 * A server the user has asked the dashboard to keep an eye on. The
 * monitor probes it periodically:
 *   1. HTTPS reachability + status code
 *   2. SSL certificate validity + expiry
 *   3. The XOP product `/api/v2/get_site_params` endpoint, if present —
 *      anything we can pull (dial-in numbers, version, etc.) gets stored
 *      in `params`. If the endpoint isn't there or fails, that's fine —
 *      we just leave `params` undefined and the UI shows the row without
 *      that column.
 */
export interface MonitoredServer {
  id: number;
  label: string;
  url: string;
  /** Optional grouping for the UI (e.g. "Conferencing", "API"). */
  group: string | null;
  created_at: string;

  /** Last health-probe result, populated by the background monitor. */
  last_checked_at: string | null;
  reachable: boolean | null;
  http_status: number | null;
  latency_ms: number | null;

  ssl: SslStatus | null;
  params: SiteParams | null;
  last_error: string | null;
}

export interface SslStatus {
  valid: boolean;
  expires_at: string | null;
  issuer: string | null;
  /** Days until expiry — negative means already expired. Server-side
   * computes this so every client renders the same number. */
  days_remaining: number | null;
}

/** Best-effort shape for the XOP product `get_site_params` response.
 * Anything we don't recognise is stashed in `extra` so the UI can show
 * unknown keys without us having to ship a contract change. */
export interface SiteParams {
  version: string | null;
  dial_in_numbers: string[];
  site_name: string | null;
  /** Any other top-level keys returned by the upstream. */
  extra: Record<string, unknown>;
}

export interface AddMonitoredServerRequest {
  label: string;
  url: string;
  group?: string;
}

/** Edit an existing monitored server. All fields optional — only the
 * ones that are present get updated. */
export interface UpdateMonitoredServerRequest {
  label?: string;
  url?: string;
  group?: string | null;
}

export interface MonitoredServersList {
  servers: MonitoredServer[];
  /** When the server last completed a full polling sweep — null if the
   * monitor hasn't done its first pass yet. */
  last_sweep_at: string | null;
}

/** Persist the user's preferred order. Server stores `display_order` per
 * row; the wire shape is just the ids in their new order. Anything not
 * mentioned keeps its current position. */
export interface ReorderMonitoredServersRequest {
  ids: number[];
}

/* ------------------------------------------------------------------
 * Server status history
 *
 * Append-only feed of state transitions for the user-curated monitored
 * servers. The Server Status page renders this as a right-hand
 * timeline; only *transitions* are recorded (a server going down or
 * recovering), never every successful probe.
 * ------------------------------------------------------------------ */

export type ServerEventType = "down" | "recovered";

export interface ServerEvent {
  id: number;
  server_id: number;
  /** Snapshotted via JOIN so the row stays renderable even if the
   * server has since been removed from the monitored list. */
  server_label: string | null;
  server_url: string | null;
  event_type: ServerEventType;
  http_status: number | null;
  latency_ms: number | null;
  error: string | null;
  occurred_at: string;
}

export interface ServerEventsList {
  events: ServerEvent[];
  page: number;
  per_page: number;
  total: number;
  /** The lookback window the server used to filter results, in days. */
  days: number;
}
